import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDimensions } from '../contexts/DimensionContext';
import { useBook } from '../contexts/BookContext';
import PDFPreviewLayout from '../components/PDFPreview/PDFPreviewLayout';
import CoverSpreadPreview from '../components/PDFPreview/components/preview/CoverSpreadPreview';
import { usePDFGeneration } from '../hooks/usePDFGeneration';
import { notify, toastIds } from '../utils/notify.jsx';
import TutorialModal from '../components/TutorialSlideshow/TutorialModal';

export default function PrintPage() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const { currentBook, loadBook } = useBook();
  const { selectedPageSize, updatePageSize, setCurrentBookPageSize } = useDimensions();
  const [debugMode, setDebugMode] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [autoGenerationStatus, setAutoGenerationStatus] = useState({
    cover: 'pending',
    verticalContent: 'pending',
    horizontalContent: 'pending'
  });
  const [forcedRegeneration, setForcedRegeneration] = useState({
    cover: false,
    verticalContent: false,
    horizontalContent: false
  });

  useEffect(() => {
    if (bookId) {
      loadBook(bookId);
      
      // Always mark the cover PDF for forced regeneration on initial load
      // This ensures correct Lulu dimensions even on page refresh
      setForcedRegeneration(prev => ({ ...prev, cover: true }));
      
      // On initial load, immediately load page size from local storage if it exists
      const storedPageSize = localStorage.getItem(`book_page_size_${bookId}`);
      if (storedPageSize) {
        console.log(`PrintPage: Found stored page size ${storedPageSize} for book ${bookId}`); 
        updatePageSize(storedPageSize, bookId);
      } else {
        console.log(`PrintPage: No stored page size found for book ${bookId}`);
      }
    }
  }, [bookId, loadBook, updatePageSize]);
  
  // Synchronize page size when book data is loaded
  useEffect(() => {
    if (currentBook?.id && currentBook.pageSize) {
      console.log(`PrintPage: Synchronizing with book page size: ${currentBook.pageSize}`);
      
      // Ensure consistency across steps
      setCurrentBookPageSize(currentBook.id, currentBook.pageSize);
      updatePageSize(currentBook.pageSize, currentBook.id);
      
      // Store for persistence
      localStorage.setItem(`book_page_size_${currentBook.id}`, currentBook.pageSize);
      sessionStorage.setItem('current_page_size', currentBook.pageSize);
    }
  }, [currentBook?.id, currentBook?.pageSize, updatePageSize, setCurrentBookPageSize]);

  const { generatePDF, generateCoverPDF, generateInteriorPDF, generateVerticalInteriorPDF, isGenerating, error } = usePDFGeneration(currentBook);
  const [localPdfUrls, setLocalPdfUrls] = useState({
    cover: null,
    verticalContent: null,
    horizontalContent: null,
  });

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      try {
        if (localPdfUrls.cover) URL.revokeObjectURL(localPdfUrls.cover);
        if (localPdfUrls.verticalContent) URL.revokeObjectURL(localPdfUrls.verticalContent);
        if (localPdfUrls.horizontalContent) URL.revokeObjectURL(localPdfUrls.horizontalContent);
      } catch (_) {}
    };
  }, [localPdfUrls.cover, localPdfUrls.verticalContent, localPdfUrls.horizontalContent]);

  const handleRegeneratePDF = async (pdfType) => {
    if (isGenerating) return;

    try {
      if (pdfType === 'verticalContent') {
        const resp = await generateVerticalInteriorPDF(false, { returnBlob: true });
        if (resp && typeof resp === 'object' && 'blob' in resp) {
          setLocalPdfUrls(prev => {
            if (prev.verticalContent) URL.revokeObjectURL(prev.verticalContent);
            return { ...prev, verticalContent: URL.createObjectURL(resp.blob) };
          });
        }
      } else if (pdfType === 'horizontalContent') {
        const resp = await generateInteriorPDF(false, { returnBlob: true });
        if (resp && typeof resp === 'object' && 'blob' in resp) {
          setLocalPdfUrls(prev => {
            if (prev.horizontalContent) URL.revokeObjectURL(prev.horizontalContent);
            return { ...prev, horizontalContent: URL.createObjectURL(resp.blob) };
          });
        }
      } else if (pdfType === 'cover') {
        // Always use forLuluPrinting: true for cover PDF to ensure exact Lulu dimensions
        console.log('📏 Regenerating cover preview (blob only) with exact Lulu dimensions');
        const resp = await generateCoverPDF(false, { forLuluPrinting: true, returnBlob: true });
        if (resp && typeof resp === 'object' && 'blob' in resp) {
          setLocalPdfUrls(prev => {
            if (prev.cover) URL.revokeObjectURL(prev.cover);
            return { ...prev, cover: URL.createObjectURL(resp.blob) };
          });
        }
      }
    } catch (err) {
      console.error(`Error regenerating ${pdfType} PDF:`, err);
      notify.error(`Failed to regenerate ${pdfType} PDF`, { toastId: toastIds.printPdf(bookId, pdfType) });
      setAutoGenerationStatus(prev => ({ ...prev, [pdfType]: 'error' }));
    }
  };


  // Disabled: no PDF generation on Print step. We only show fast local previews.

  useEffect(() => {
    const handleDebugModeChange = (event) => {
      if (event.detail) {
        setDebugMode(event.detail.debugMode);
      }
    };
    window.addEventListener('debugModeChange', handleDebugModeChange);
    return () =>
      window.removeEventListener('debugModeChange', handleDebugModeChange);
  }, []);

  const handleContinueToShip = () => {
    navigate(`/ship/${bookId}`);
  };

  const handleBackToDesign = () => {
    navigate(`/design/${bookId}`);
  };

  // Function to add timestamp for cache-busting (skip for blob: URLs)
  const getCacheBustedUrl = (url) => {
    if (!url) return null;
    if (typeof url === 'string' && url.startsWith('blob:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${Date.now()}`;
  };

  if (!currentBook) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">
          No book found. Please start from the create page.
        </p>
        <button
          onClick={() => loadBook(bookId)}
          className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
        >
          Refetch Book
        </button>
        <button
          onClick={() => navigate('/create')}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Go to Create Page
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Loading overlay removed: we render instant local previews. */}
      
      <div className="px-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Print</h1>
            <p className="text-gray-600">Preview and download print-ready files for your book.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowTutorialModal(true)}
              className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-900 hover:bg-yellow-200 font-medium"
            >
              Tutorial
            </button>
            <button
              onClick={handleBackToDesign}
              className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-50"
            >
              Back to Design
            </button>
            <button
              onClick={handleContinueToShip}
              className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Continue to Ship
            </button>
          </div>
        </div>
        {/* Status panel and direct cover iframe removed. */}
      </div>

      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => setShowTutorialModal(false)}
        defaultTabId={5}
      />

      {/* Cover Preview (Exterior) */}
      <div className="px-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Cover Preview (Exterior)</h2>
          <button
            onClick={async () => {
              try {
                // Generate and immediately download cover PDF (no upload)
                await generateCoverPDF(false, { forLuluPrinting: true });
              } catch (e) {
                console.error('Cover download failed:', e);
              }
            }}
            disabled={isGenerating}
            className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            Download
          </button>
        </div>
        <div className="rounded-lg shadow-md border border-gray-200 bg-white p-4">
          <div className="rounded overflow-hidden flex justify-center items-center" style={{ height: '500px' }}>
            <CoverSpreadPreview
              spread={currentBook?.spreads?.['spread-0']}
              book={currentBook}
              scale={0.4}
              canvasMode={'design'}
            />
          </div>
        </div>
      </div>
      
      <PDFPreviewLayout
        book={currentBook}
        onPrint={() => {}}
        debugMode={debugMode}
        savedPdfUrls={localPdfUrls}
        previewOnly={true}
        forceRefresh={isInitialLoading}
      />
    </div>
  );
}
